import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { API, axios } from '../../utils/api';
import {
  aggiungiGiorni, dataLontana, differenzaGiorni, domenica, giornoTesto, isoGiorno, limitiData, messaggioErrore,
  quandoRichiamo, saltaDomenica, ANNI_AGENDA,
} from '../../utils/agenda';
import { adessoRoma } from '../../utils/percorso';
import FissaVisita from './FissaVisita';

// Esito della telefonata all'agenzia, a sette bottoni (agenda, Oggi, scheda cliente, scheda agenzia). Gli effetti li
// fa il backend (backend/telefonate.py): richiamo automatico, visita con avvisi al cliente e all'agenzia, scarto,
// agenzia non lavorabile, cronologie, statistiche di risposta.

// `nota`: l'esito ha un campo nota (o "Motivo") in pagina. Gli altri non ce l'hanno e la nota non gliela si manda:
// scritta in "Richiamare" e poi premuto "Non rispondono", partiva lo stesso e il server la buttava via in silenzio;
// passando a "Già venduto" la stessa nota ricompariva come "Motivo" e finiva nella cronologia del cliente come
// motivo dello scarto. La nota è di quell'esito: cambiando bottone si svuota.
const BOTTONI = [
  { id: 'non_rispondono', titolo: 'Non rispondono', sotto: 'richiamo automatico', colore: 'var(--warn)' },
  { id: 'richiamare', titolo: 'Richiamare', sotto: 'scelgo io quando', colore: 'var(--warn)', nota: true },
  { id: 'attesa_collega', titolo: 'Aspettare il collega', sotto: 'in attesa di risposta', colore: 'var(--info)', immobile: true, nota: true },
  { id: 'attesa_disponibilita', titolo: 'In attesa di disponibilità', sotto: 'del cliente, con scadenza', colore: 'var(--warn)', immobile: true, nota: true },
  { id: 'visita_fissata', titolo: 'Visita fissata', sotto: 'data, ora, chi va', colore: 'var(--ok)', immobile: true },
  { id: 'gia_venduto', titolo: 'Già venduto', sotto: 'immobile scartato', colore: 'var(--mute)', immobile: true, nota: true },
  { id: 'non_collaborano', titolo: 'Non collaborano', sotto: 'agenzia non lavorabile', colore: 'var(--bad)', admin: true, nota: true },
];
// gli esiti che in pagina hanno il campo nota: solo da questi la nota parte verso il server
export const CON_NOTA = BOTTONI.filter((b) => b.nota).map((b) => b.id);

const ora = (d) => `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;

// Quanto della nota (e del motivo) il server tiene: `A.testo_o_400(dati.get("nota"), "La nota", 500)` in
// backend/telefonate.py. I campi non avevano nessun limite: il server tagliava in silenzio e la nota finiva mozzata
// a metà parola nel testo dell'esito, nella cronologia e nell'email.
export const MAX_NOTA = 500;

// "domani stessa ora", "tra 2 ore", "lunedì alle 10" nell'ora di Roma (adessoRoma), come le date che il server
// salva: con il telefono su un fuso avanti (Auckland) "domani" era il giorno dopo ancora e "tra 2 ore" l'ora del
// telefono. Sul fuso di Roma non cambia niente.
// Nessuna scelta cade di domenica, quando le agenzie sono chiuse (il richiamo automatico del server la salta già,
// backend/telefonate.py): di sabato "domani stessa ora" diventa "lunedì stessa ora" (e lo dice), e "tra 2 ore" non
// compare quando le due ore finiscono nella domenica. "lunedì alle 10" è già di lunedì.
export const scelteRichiamo = (adesso = adessoRoma(), oraVoce = null) => {
  const oggi = isoGiorno(adesso);
  const traDue = new Date(adesso.getTime() + 2 * 3600000);
  const lunedi = aggiungiGiorni(oggi, ((8 - adesso.getDay()) % 7) || 7);
  const domani = saltaDomenica(aggiungiGiorni(oggi, 1));
  const dueOre = isoGiorno(traDue);
  return [
    { id: 'domani', etichetta: differenzaGiorni(domani, oggi) === 1 ? 'domani stessa ora' : 'lunedì stessa ora',
      data: domani, ora: oraVoce || ora(adesso) },
    ...(domenica(dueOre) ? [] : [{ id: 'due_ore', etichetta: 'tra 2 ore', data: dueOre, ora: ora(traDue) }]),
    { id: 'lunedi', etichetta: 'lunedì alle 10', data: lunedi, ora: '10:00' },
    { id: 'scegli', etichetta: 'scegli…' },
  ];
};

// ------------------------------------------------------------------ una telefonata, più case
// Quando il cliente ha due o più case con la stessa agenzia si chiama una volta sola: una riga per casa, ognuna
// con il suo esito e i suoi campi, e un solo "Salva tutto" che manda POST /telefonate/esito-multiplo.
// Sulla riga "visita fissata" NON si apre FissaVisita: tre calendari uno sull'altro non si usano, quindi si
// chiede solo giorno e ora (lo slot preciso si sceglie con "Fissa visita" dalla card della casa).

const CON_ORA = ['richiamare', 'visita_fissata'];
const CON_ENTRO = ['attesa_collega', 'attesa_disponibilita'];
const RIGA_VUOTA = { esito: null, quando: 'domani', giorno: '', orario: '', entro: '2', nota: '' };
const etichettaCasa = (c) => c.indirizzo || c.titolo || 'questa casa';

const EsitoMultiplo = ({ voce, case: lista, onFatto, isAdmin, nomeCliente, nomeAgenzia }) => {
  const [righe, setRighe] = useState({});
  const [attesa, setAttesa] = useState(false);
  const [errore, setErrore] = useState('');
  const scelte = useMemo(() => scelteRichiamo(new Date(), voce.ora), [voce.ora]);
  const bottoni = BOTTONI.filter((b) => !b.admin || isAdmin);
  const riga = (id) => righe[id] || RIGA_VUOTA;
  const cambia = (id, dati) => setRighe((r) => ({ ...r, [id]: { ...(r[id] || RIGA_VUOTA), ...dati } }));
  const compilate = lista.filter((c) => riga(c.id).esito);
  const agenzia = nomeAgenzia || voce.agenzia_nome || (voce.agenzia || {}).nome || '';

  const salva = async () => {
    const voci = [];
    for (const c of compilate) {
      const r = riga(c.id);
      const v = { immobile_id: c.id, esito: r.esito, nota: r.nota || undefined };
      if (r.esito === 'richiamare') {
        const s = scelte.find((x) => x.id === r.quando);
        v.data = r.quando === 'scegli' ? r.giorno : s.data;
        v.ora = r.quando === 'scegli' ? r.orario : s.ora;
      }
      if (r.esito === 'visita_fissata') { v.data = r.giorno; v.ora = r.orario; }
      if (CON_ORA.includes(r.esito) && (!v.data || !v.ora)) {
        const m = `Scegliete giorno e ora per ${etichettaCasa(c)}`;
        setErrore(m); toast.error(m); return;
      }
      if (CON_ENTRO.includes(r.esito)) v.entro = aggiungiGiorni(isoGiorno(new Date()), Number(r.entro));
      voci.push(v);
    }
    setAttesa(true); setErrore('');
    try {
      const r = await axios.post(`${API}/telefonate/esito-multiplo`, {
        cliente_id: voce.cliente_id || undefined, agenzia_id: voce.agenzia_id || undefined, voci,
      });
      toast.success(r.data.riepilogo || r.data.testo || 'Esiti salvati');
      setRighe({});
      if (onFatto) onFatto(r.data);
    } catch (e) {
      // se il server rifiuta non ha scritto niente: la finestra resta aperta com'era, con le scelte già fatte
      const m = typeof e.response?.data?.detail === 'string' ? e.response.data.detail : 'Non riuscito';
      setErrore(m); toast.error(m);
    } finally { setAttesa(false); }
  };

  const seguito = (c) => {
    const r = riga(c.id);
    const nota = (testo) => <>nota <input value={r.nota} onChange={(e) => cambia(c.id, { nota: e.target.value })} placeholder={testo} aria-label="Nota" /></>;
    if (r.esito === 'non_rispondono') return <div className="follow">Richiamo automatico domani alla stessa ora. {nota('facoltativa')}</div>;
    if (r.esito === 'richiamare') {
      return (
        <div className="follow">
          Richiamo
          <select value={r.quando} onChange={(e) => cambia(c.id, { quando: e.target.value })} aria-label="Quando richiamare">
            {scelte.map((s) => <option key={s.id} value={s.id}>{s.etichetta}</option>)}
          </select>
          {r.quando === 'scegli' && <><input type="date" value={r.giorno} onChange={(e) => cambia(c.id, { giorno: e.target.value })} aria-label="Giorno" /><input type="time" value={r.orario} onChange={(e) => cambia(c.id, { orario: e.target.value })} aria-label="Ora" /></>}
          {nota('es. chiedere di Chiara')}
        </div>
      );
    }
    if (CON_ENTRO.includes(r.esito)) {
      return (
        <div className="follow">
          {r.esito === 'attesa_collega' ? 'Attendo risposta entro' : `Aspetto la disponibilità di ${nomeCliente || voce.cliente_breve || 'del cliente'} entro`}
          <select value={r.entro} onChange={(e) => cambia(c.id, { entro: e.target.value })} aria-label="Entro quando">
            <option value="1">domani</option><option value="2">2 giorni</option><option value="7">1 settimana</option>
          </select>
          {nota('facoltativa')}
        </div>
      );
    }
    if (r.esito === 'visita_fissata') {
      return (
        <div className="follow">
          Visita il <input type="date" value={r.giorno} onChange={(e) => cambia(c.id, { giorno: e.target.value })} aria-label="Giorno" />
          alle <input type="time" value={r.orario} onChange={(e) => cambia(c.id, { orario: e.target.value })} aria-label="Ora" />
          {nota('facoltativa')}
          <span className="muto" style={{ width: '100%', fontSize: '.74rem' }}>Per vedere il calendario e scegliere lo slot libero usate "Fissa visita" dalla card della casa.</span>
        </div>
      );
    }
    if (r.esito === 'gia_venduto' || r.esito === 'non_collaborano') {
      return <div className="follow">Motivo <input value={r.nota} onChange={(e) => cambia(c.id, { nota: e.target.value })} placeholder="facoltativo" aria-label="Motivo" /></div>;
    }
    return null;
  };

  return (
    <div className="esito multi" data-testid="esito-multiplo">
      <div className="q">Com'è andata{agenzia ? ` con ${agenzia}` : ''}? {lista.length} case</div>
      {lista.map((c) => (
        <div className="casa" key={c.id} data-testid={`riga-casa-${c.id}`}>
          <div className="cap"><b>{etichettaCasa(c)}</b>{c.titolo && c.indirizzo && c.titolo !== c.indirizzo ? <span>{c.titolo}</span> : null}</div>
          <div className="esiti">
            {bottoni.map((b) => {
              const on = riga(c.id).esito === b.id;
              return (
                <button key={b.id} type="button" className={`big${on ? ' on' : ''}`} aria-pressed={on}
                  onClick={() => cambia(c.id, { esito: on ? null : b.id })}>
                  <i style={{ background: b.colore }} /><b>{b.titolo}</b><span>{b.sotto}</span>
                </button>
              );
            })}
          </div>
          {seguito(c)}
        </div>
      ))}
      {errore && <div className="follow"><span className="avviso" role="alert">{errore}</span></div>}
      <div className="follow fine">
        <button type="button" className="btn p" disabled={attesa || !compilate.length} onClick={salva}>Salva tutto</button>
        <span className="muto">{compilate.length ? `${compilate.length} di ${lista.length} case` : 'Scegliete l’esito di almeno una casa'}</span>
      </div>
    </div>
  );
};

// Le scadenze di "Aspettare il collega" e "In attesa di disponibilità": la tendina diceva "domani / 2 giorni /
// 1 settimana" senza dire in che giorno si finiva, e il giorno poteva essere una domenica, quando le agenzie sono
// chiuse (di sabato "domani" era domenica; "2 giorni" da venerdì pure). Come per il richiamo: la domenica si salta
// (`saltaDomenica`), "domani" diventa "lunedì" quando è lunedì, e accanto alla tendina si legge la data vera.
export const scelteEntro = (adesso = adessoRoma()) => {
  const oggi = isoGiorno(adesso);
  const quando = (n) => saltaDomenica(aggiungiGiorni(oggi, n));
  const domani = quando(1);
  return [
    { id: '1', etichetta: differenzaGiorni(domani, oggi) === 1 ? 'domani' : 'lunedì', data: domani },
    { id: '2', etichetta: '2 giorni', data: quando(2) },
    { id: '7', etichetta: '1 settimana', data: quando(7) },
  ];
};

// Il richiamo automatico di "Non rispondono", calcolato come lo calcola il server (backend/telefonate.py,
// `domani_stessa_ora`): il giorno dopo la telefonata programmata (se non è già passata; se no, il giorno dopo oggi),
// alla stessa ora, saltando la domenica. Il riquadro diceva sempre "domani alla stessa ora", ma di sabato il server
// sposta a lunedì.
export const richiamoAutomatico = (voce = {}, adesso = adessoRoma()) => {
  const oggi = isoGiorno(adesso);
  // la telefonata come l'ha programmata il server: la chiave della proiezione porta `data_prossimo_contatto`
  // dell'immobile ("tel:<immobile>:AAAAMMGGHHMM", backend/agenda.py), che è quello da cui il server riparte;
  // se non c'è, il giorno e l'ora della voce
  const chiave = /^tel:.+:(\d{8})(\d{4})$/.exec(voce.rif?.proiezione || voce.id || '');
  const dataVoce = chiave ? `${chiave[1].slice(0, 4)}-${chiave[1].slice(4, 6)}-${chiave[1].slice(6, 8)}` : voce.data;
  const oraVoce = chiave ? `${chiave[2].slice(0, 2)}:${chiave[2].slice(2)}` : voce.ora;
  const programmata = dataVoce && oraVoce && String(dataVoce) >= oggi;
  const base = programmata ? String(dataVoce) : oggi;
  const dopo = aggiungiGiorni(base, 1);
  return { data: saltaDomenica(dopo), ora: programmata ? oraVoce : ora(adesso), oggi, salta: domenica(dopo) };
};

// Il richiamo scelto a mano ("scegli…") può essere già passato: il server lo rifiuta con un 400 ("Il richiamo non
// può essere nel passato", backend/telefonate.py), come rifiuta la visita nel passato di "Fissa visita". Il giorno
// prima di oggi, o l'ora di oggi già passata, sono "passato"; senza l'ora si guarda il solo giorno (a "Salva" ci
// pensa il controllo di giorno+ora). Il confronto è nell'ora di Roma (`adesso` viene da `adessoRoma`), come in
// `utils/fissaVisita.js` (`passato`).
export const richiamoPassato = (data, ora, adesso) => {
  if (!data) return false;
  const oggi = isoGiorno(adesso);
  if (String(data) < oggi) return true;
  if (String(data) > oggi || !ora) return false;
  const quando = new Date(`${data}T${String(ora).slice(0, 5)}:00`);
  return !Number.isNaN(quando.getTime()) && quando <= adesso;
};

// L'orologio di Roma del riquadro, come quello delle pagine (pages/staff/{Oggi,Agenda}.jsx): le scelte erano
// calcolate una volta sola e a mezzanotte (o passata l'ora) restavano al giorno prima — la tendina diceva "domani
// stessa ora" mentre la riga sotto diceva già "→ oggi alle 10:30", ed era oggi quello che si salvava.
const OGNI_MEZZO_MINUTO = 30000;

const EsitoTelefonata = ({ voce = {}, onFatto, isAdmin = true, nomeCliente, case: lista, nomeAgenzia: nomeAgenziaDato, altreCase = null }) => {
  // le altre case che quel cliente ha con la stessa agenzia: si spuntano e l'esito si dà per tutte insieme
  const altre = altreCase || { quante: 0, case: [], gruppo: [] };
  const [spuntate, setSpuntate] = useState([]);
  const [allargato, setAllargato] = useState(null);
  const spunta = (id) => setSpuntate((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const [scelto, setScelto] = useState(null);
  const [attesa, setAttesa] = useState(false);
  const [nota, setNota] = useState('');
  // il taglio vale anche per il testo incollato da fuori, che passa sopra `maxLength`
  const scriviNota = (testo) => setNota(String(testo).slice(0, MAX_NOTA));
  const [quando, setQuando] = useState('domani');
  const [giorno, setGiorno] = useState('');
  const [orario, setOrario] = useState('');
  const [entro, setEntro] = useState('2');
  const immobileId = voce.immobile_id;
  const conImmobile = Boolean(immobileId);
  // il nome dell'agenzia che si sta chiamando, per dire nel bottone chi si spegne ("Segna Zetaci non lavorabile")
  // il nome che passa chi ci apre (la scheda dell'agenzia lo sa già), se no quello che sta dentro la voce
  const nomeAgenzia = nomeAgenziaDato || voce.cosa?.agenzia?.nome || voce.agenzia?.nome || voce.agenzia_nome || "l'agenzia";
  // le date del riquadro seguono l'orologio di Roma: al cambio di minuto (e quindi a mezzanotte) si rifanno tutte
  // insieme, invece di restare al giorno in cui il riquadro si era aperto
  const [adesso, setAdesso] = useState(adessoRoma);
  useEffect(() => {
    const t = setInterval(() => setAdesso((prec) => {
      const ora2 = adessoRoma();
      return ora2.getMinutes() === prec.getMinutes() && ora2.getDate() === prec.getDate() ? prec : ora2;
    }), OGNI_MEZZO_MINUTO);
    return () => clearInterval(t);
  }, []);
  const scelte = useMemo(() => scelteRichiamo(adesso, voce.ora), [adesso, voce.ora]);
  const richiamo = useMemo(() => richiamoAutomatico(voce, adesso), [voce, adesso]);
  const entroScelte = useMemo(() => scelteEntro(adesso), [adesso]);
  const oggiRoma = isoGiorno(adesso);
  const sceltaQuando = scelte.find((x) => x.id === quando);
  const sceltaEntro = entroScelte.find((x) => x.id === entro) || entroScelte[0];
  // il giorno e l'ora del richiamo, comunque scelti: dalla tendina o scritti a mano con "scegli…". Da qui la riga
  // "→ …" (che prima con "scegli…" non c'era: si sceglieva una data senza leggerla scritta) e il controllo del
  // passato, che spegne "Salva" invece di far arrivare il 400 del server
  const dataRichiamo = quando === 'scegli' ? giorno : sceltaQuando?.data;
  const oraRichiamo = quando === 'scegli' ? orario : sceltaQuando?.ora;
  const richiamoVecchio = richiamoPassato(dataRichiamo, oraRichiamo, adesso);
  // un richiamo oltre i cinque anni il server lo rifiuta (400 "Data troppo lontana"): il campo ha il `max`, e se la
  // data ci arriva lo stesso (scritta a mano) "Salva" resta spento invece di promettere il 2045
  const richiamoLontano = dataLontana(dataRichiamo, oggiRoma);
  // le agenzie la domenica sono chiuse: tutte le altre strade la saltano (`saltaDomenica`), la scelta a mano no —
  // almeno lo dice, invece di far fissare un richiamo a saracinesche abbassate
  const richiamoDiDomenica = quando === 'scegli' && Boolean(dataRichiamo) && !richiamoLontano && domenica(dataRichiamo);
  const limiti = limitiData(oggiRoma);
  // "tra 2 ore" sparisce quando le due ore finiscono di domenica: se era quello scelto, si torna alla prima scelta
  useEffect(() => { if (quando !== 'scegli' && !scelte.some((s) => s.id === quando)) setQuando(scelte[0].id); }, [scelte, quando]);
  // "Visita fissata": la finestra con il calendario, che salva l'esito (visita, avvisi, conferma all'agenzia)
  const esitoVisita = async (dati) => (await axios.post(`${API}/telefonate/esito`, {
    esito: 'visita_fissata', immobile_id: immobileId, agenzia_id: voce.agenzia_id || undefined, voce_id: voce.id, ...dati,
  })).data;

  // si cambia esito: la nota scritta per quello di prima non passa a quello dopo (era la nota di quella scelta, e
  // sotto un altro bottone diventava un'altra cosa — il "Motivo" dello scarto, o niente)
  const scegli = (id) => {
    if (id !== scelto) setNota('');
    setScelto(id);
  };

  const invia = async () => {
    // la nota parte solo dagli esiti che in pagina hanno il campo: negli altri il server la butta via in silenzio
    const conNota = CON_NOTA.includes(scelto);
    const corpo = { esito: scelto, immobile_id: immobileId || undefined, agenzia_id: voce.agenzia_id || undefined, voce_id: voce.id, nota: (conNota && nota) || undefined };
    if (scelto === 'richiamare') {
      corpo.data = dataRichiamo;
      corpo.ora = oraRichiamo;
      if (!corpo.data || !corpo.ora) { toast.error('Scegliete giorno e ora'); return; }
      // l'ora può passare mentre il riquadro resta aperto: si ricontrolla sull'orologio di adesso, non su quello
      // dell'ultimo giro (il server rifiuterebbe con un 400)
      if (richiamoPassato(corpo.data, corpo.ora, adessoRoma())) return;
      if (dataLontana(corpo.data, isoGiorno(adessoRoma()))) return;
    }
    if (scelto === 'attesa_collega' || scelto === 'attesa_disponibilita') corpo.entro = sceltaEntro.data;
    setAttesa(true);
    try {
      const r = await axios.post(`${API}/telefonate/esito`, corpo);
      toast.success(r.data.testo || 'Esito salvato');
      setScelto(null);
      if (onFatto) onFatto(r.data);
    } catch (e) {
      toast.error(messaggioErrore(e, 'Esito non salvato. Riprovate.'));
    } finally { setAttesa(false); }
  };

  // due o più case della stessa agenzia: il pannello diventa multiplo. Con una casa sola (o senza la prop) resta
  // identico a com'era, rotta /telefonate/esito compresa: si apre da sei posti e nessuno deve cambiare.
  if ((Array.isArray(lista) && lista.length > 1) || allargato) {
    return <EsitoMultiplo voce={voce} case={allargato || lista} onFatto={onFatto} isAdmin={isAdmin}
      nomeCliente={nomeCliente} nomeAgenzia={nomeAgenzia} />;
  }

  const bottoni = BOTTONI.filter((b) => (!b.immobile || conImmobile) && (!b.admin || isAdmin));
  return (
    <div className="esito" data-testid="esito-telefonata">
      {/* Quella casa non è sola: si sta telefonando a un'agenzia che ne ha altre per questo cliente. Chiederlo
          qui costa un clic; non chiederlo costa un'altra telefonata (backend/agenzia_case.py). */}
      {altre.quante > 0 && (
        <div className="altre-in-esito" data-testid="esito-altre-case">
          <b>{altre.etichetta} ha altre {altre.quante === 1 ? 'una casa' : `${altre.quante} case`} per {nomeCliente || 'questo cliente'}:
            vuoi dare l&apos;esito anche per quelle?</b>
          <ul>
            {altre.case.map((c) => (
              <li key={c.id}>
                <label>
                  <input type="checkbox" checked={spuntate.includes(c.id)} onChange={() => spunta(c.id)} />
                  {c.indirizzo || c.titolo}
                </label>
              </li>
            ))}
          </ul>
          <button type="button" className="btn sm p" disabled={!spuntate.length} data-testid="esito-allarga"
            onClick={() => setAllargato([...(altre.gruppo || []).filter((c) => c.id === voce.immobile_id || spuntate.includes(c.id))])}>
            Sì, dai l&apos;esito per {spuntate.length + 1} cas{spuntate.length ? 'e' : 'a'}
          </button>
        </div>
      )}
      <div className="q">Com'è andata?</div>
      <div className="esiti">
        {bottoni.map((b) => (
          <button key={b.id} type="button" className={`big${scelto === b.id ? ' on' : ''}`} onClick={() => scegli(b.id)} aria-pressed={scelto === b.id}>
            <i style={{ background: b.colore }} /><b>{b.titolo}</b><span>{b.sotto}</span>
          </button>
        ))}
      </div>
      {scelto === 'non_rispondono' && (
        <div className="follow">
          {/* la data vera, calcolata come la calcola il server (richiamoAutomatico): di sabato il richiamo è di
              lunedì, non "domani" */}
          <span data-testid="quando-automatico">
            Richiamo automatico {quandoRichiamo(richiamo.data, richiamo.ora, richiamo.oggi)}
            {richiamo.salta ? ' (la domenica le agenzie sono chiuse)' : ''}
            {/* il numero è quello che scrive il server salvando l'esito (backend/telefonate.py: `tentativi += 1`),
                cioè questa telefonata qui: lo stesso di `etichettaTipo` e della riga della card (`tentativi + 1`).
                Con `+ 2` il riquadro prometteva "sarà il 13°" dove la riga diceva "12° tentativo" e il server
                scriveva "12°". */}
            {voce.tentativi ? ` · sarà il ${voce.tentativi + 1}° tentativo` : ''}.
          </span>
          <button type="button" className="btn sm p" disabled={attesa} onClick={invia}>Salva</button>
        </div>
      )}
      {scelto === 'richiamare' && (
        <div className="follow">
          Richiamo
          <select value={quando} onChange={(e) => setQuando(e.target.value)} aria-label="Quando richiamare">
            {scelte.map((s) => <option key={s.id} value={s.id}>{s.etichetta}</option>)}
          </select>
          {/* il calendario non lascia scegliere un giorno già passato né uno oltre la finestra del server (cinque
              anni): il richiamo nel passato e quello troppo lontano finivano tutti e due in un 400 dopo il clic */}
          {quando === 'scegli' && <><input type="date" value={giorno} min={oggiRoma} max={limiti.max} onChange={(e) => setGiorno(e.target.value)} aria-label="Giorno" /><input type="time" value={orario} onChange={(e) => setOrario(e.target.value)} aria-label="Ora" /></>}
          {/* la data vera della scelta: "domani stessa ora" di sabato è lunedì, e "tra 2 ore" può essere di un
              altro giorno; con "scegli…" è il giorno scritto a mano, che prima non si leggeva scritto. Una data che
              il server non prende non si scrive come se fosse fissata */}
          {dataRichiamo && !richiamoLontano && <span className="muto" data-testid="quando-scelto">→ {quandoRichiamo(dataRichiamo, oraRichiamo, oggiRoma)}{richiamoDiDomenica ? ' · la domenica le agenzie sono chiuse' : ''}</span>}
          {/* il campo si ferma a quello che il server tiene (MAX_NOTA): oltre tagliava lui in silenzio */}
          nota <input value={nota} onChange={(e) => scriviNota(e.target.value)} maxLength={MAX_NOTA} placeholder="es. chiedere di Chiara" aria-label="Nota" />
          <button type="button" className="btn sm p" disabled={attesa || richiamoVecchio || richiamoLontano} onClick={invia}>Salva</button>
          {/* un bottone che non può funzionare non si mostra acceso: stessa frase di "Fissa visita" */}
          {richiamoVecchio && (
            <span className="avviso" data-testid="richiamo-passato">
              Quest'ora è passata: scegliete un momento più avanti (un richiamo nel passato il server non lo accetta).
            </span>
          )}
          {richiamoLontano && (
            <span className="avviso" data-testid="richiamo-lontano">
              Questo giorno è troppo lontano: l'agenda arriva a {ANNI_AGENDA} anni da oggi.
            </span>
          )}
          {/* la domenica non spegne "Salva" (si può volere lo stesso), ma non si tace: tutte le altre strade la saltano */}
          {richiamoDiDomenica && (
            <span className="avviso" data-testid="richiamo-domenica">
              Quel giorno è domenica: le agenzie sono chiuse.
            </span>
          )}
        </div>
      )}
      {scelto === 'attesa_collega' && (
        <div className="follow">
          Attendo risposta entro
          <select value={entro} onChange={(e) => setEntro(e.target.value)} aria-label="Entro quando">
            {entroScelte.map((s) => <option key={s.id} value={s.id}>{s.etichetta}</option>)}
          </select>
          {/* la data vera della scadenza, come per "Richiamare": la tendina diceva solo "2 giorni" */}
          <span className="muto" data-testid="entro-scelto">→ {giornoTesto(sceltaEntro.data, oggiRoma)}</span>
          <input value={nota} onChange={(e) => scriviNota(e.target.value)} maxLength={MAX_NOTA} placeholder="nota (facoltativa)" aria-label="Nota" />
          <button type="button" className="btn sm p" disabled={attesa} onClick={invia}>Salva</button>
        </div>
      )}
      {scelto === 'attesa_disponibilita' && (
        <div className="follow">
          Aspetto la disponibilità di {nomeCliente || voce.cliente_breve || 'del cliente'} entro
          <select value={entro} onChange={(e) => setEntro(e.target.value)} aria-label="Entro quando">
            {entroScelte.map((s) => <option key={s.id} value={s.id}>{s.etichetta}</option>)}
          </select>
          <span className="muto" data-testid="entro-scelto">→ {giornoTesto(sceltaEntro.data, oggiRoma)}</span>
          <input value={nota} onChange={(e) => scriviNota(e.target.value)} maxLength={MAX_NOTA} placeholder="nota (facoltativa)" aria-label="Nota" />
          <button type="button" className="btn sm p" disabled={attesa} onClick={invia}>Salva</button>
          <span className="muto" style={{ width: '100%', fontSize: '.74rem' }}>In agenda la scadenza; quando aggiorna la disponibilità lo vedete in "Dai clienti"; si chiude da sola quando fissate la visita.</span>
        </div>
      )}
      {scelto === 'visita_fissata' && (
        <FissaVisita clienteId={voce.cliente_id || null} clienteNome={nomeCliente || voce.cliente_breve || ''} immobileId={immobileId || ''}
          titolo="Visita fissata" invia={esitoVisita} onChiudi={() => setScelto(null)}
          onFatto={(r) => { setScelto(null); if (onFatto) onFatto(r); }} />
      )}
      {(scelto === 'gia_venduto' || scelto === 'non_collaborano') && (
        <div className="follow">
          Motivo <input value={nota} onChange={(e) => scriviNota(e.target.value)} maxLength={MAX_NOTA} placeholder="facoltativo" aria-label="Motivo" />
          {/* il bottone dice la cosa più grossa che sta per succedere: "Non collaborano" spegne l'agenzia per tutti
              i clienti, non solo questa casa, e prima diceva "Scarta l'immobile e avvisa" come "Già venduto" */}
          <button type="button" className="btn sm p" disabled={attesa} onClick={invia}>
            {scelto === 'non_collaborano' ? `Segna ${nomeAgenzia} non lavorabile${conImmobile ? ' e scarta la casa' : ''}` : "Scarta l'immobile e avvisa"}
          </button>
          {scelto === 'non_collaborano' && (
            <span className="muto" style={{ width: '100%', fontSize: '.74rem' }} data-testid="effetti-non-collaborano">
              {nomeAgenzia[0].toUpperCase() + nomeAgenzia.slice(1)} esce dalle case di tutti i clienti, non solo da questa{conImmobile ? '; la casa viene scartata e il cliente avvisato' : ''}.
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export const fascia = (hhmm) => {
  const h = Number(String(hhmm).slice(0, 2));
  return h < 13 ? 'mattina' : h < 18 ? 'pomeriggio' : 'sera';
};

export default EsitoTelefonata;
